const TOKEN_MAX_AGE_MS = 4 * 60 * 1000; // 4 min (safety buffer before Turnstile's 5-min expiry)

type TokenWaiter = {
    resolve: (token: string | null) => void;
    timer: ReturnType<typeof setTimeout>;
};

let currentToken: string | null = null;
let tokenTimestamp = 0;
let widgetId: string | null = null;
let initialized = false;
let tokenWaiters: TokenWaiter[] = [];

function flushWaiters(token: string): void {
    const waiters = tokenWaiters;
    tokenWaiters = [];
    for (const waiter of waiters) {
        clearTimeout(waiter.timer);
        waiter.resolve(token);
    }
}

function resetWidget(): void {
    currentToken = null;
    if (widgetId && window.turnstile) {
        window.turnstile.reset(widgetId);
    }
}

function isTokenStale(): boolean {
    return !currentToken || Date.now() - tokenTimestamp > TOKEN_MAX_AGE_MS;
}

function renderWidget(): void {
    const siteKey = window.TURNSTILE_SITE_KEY;
    if (!siteKey || !window.turnstile) {
        return;
    }

    const container = document.createElement('div');
    container.id = 'turnstile-container';
    container.style.display = 'none';
    document.body.appendChild(container);

    widgetId = window.turnstile.render(container, {
        sitekey: siteKey,
        callback: (token: string) => {
            currentToken = token;
            tokenTimestamp = Date.now();
            flushWaiters(token);
        },
        'expired-callback': resetWidget,
        'error-callback': () => {
            currentToken = null;
        },
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && isTokenStale()) {
            resetWidget();
        }
    });
}

/**
 * Initialize the Turnstile widget. Safe to call from any entry point —
 * handles missing site key, waits for the async script to load, and
 * no-ops if already initialized.
 */
export function initTurnstile(): void {
    if (initialized || !window.TURNSTILE_SITE_KEY) {
        return;
    }
    initialized = true;

    if (window.turnstile) {
        renderWidget();
    } else {
        const interval = setInterval(() => {
            if (window.turnstile) {
                clearInterval(interval);
                renderWidget();
            }
        }, 200);
    }
}

export function getTurnstileToken(): string | null {
    if (currentToken && Date.now() - tokenTimestamp > TOKEN_MAX_AGE_MS) {
        resetWidget();
    }
    return currentToken;
}

/**
 * Async version of getTurnstileToken that waits for a valid token.
 * Returns immediately if a valid token exists; otherwise queues a waiter
 * that resolves when the widget callback fires (or times out).
 */
export function awaitTurnstileToken(timeoutMs = 10000): Promise<string | null> {
    if (!initialized || !window.TURNSTILE_SITE_KEY) {
        return Promise.resolve(null);
    }

    if (currentToken && !isTokenStale()) {
        return Promise.resolve(currentToken);
    }

    if (currentToken) {
        resetWidget();
    }

    return new Promise<string | null>((resolve) => {
        const timer = setTimeout(() => {
            tokenWaiters = tokenWaiters.filter((w) => w.resolve !== resolve);
            resolve(null);
        }, timeoutMs);

        tokenWaiters.push({ resolve, timer });
    });
}

/** Reset all module state — only for use in tests. */
export function _resetForTesting(): void {
    currentToken = null;
    tokenTimestamp = 0;
    widgetId = null;
    initialized = false;
    for (const waiter of tokenWaiters) {
        clearTimeout(waiter.timer);
    }
    tokenWaiters = [];
}
